import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import {
    agentDashboardDataGridTabs,
    AgentDashboardStore,
} from "./AgentDashboardStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxChipList from "components/UI/AcxChipList";
import React from "react";
import { GridRenderCellParams, GridRowParams } from "@mui/x-data-grid-pro";
import { Link } from "react-router-dom";
import { Routes } from "components/Navigation/Routes";
import { Box, Button, Grid, Stack, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import {
    AgentCoaching,
    AgentCoachingTarget,
} from "models/AgentModels/AgentCoaching";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import { durationColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DurationColType";
import * as AgentCoachingService from "services/AgentCoachingService";
import { Edit } from "@mui/icons-material";
import { getInteractionType } from "components/UI/AcxDataGrid/ColumnTypes/InteractionTypeColType";

export const alreadyInDraftMessage =
    "Another user has coaching notes in draft.";

export function setAgentDashboardDataGridColumns(store: AgentDashboardStore) {
    agentDashboardDataGridTabs.forEach((tabName) => {
        const newDgStore = new AcxDataGridStore(
            tabName.toString(),
            "agentCoaching",
        );
        newDgStore.headerColumnSpan = 0;
        newDgStore.controlsColumnSpan = 12;
        if (tabName === "ReadyToCoach") {
            newDgStore.checkboxSelection = true;
            newDgStore.disableRowSelectionOnClick = true;
            newDgStore.isRowSelectable = (params: GridRowParams) =>
                store.isReadyToCoachRowSelectable(
                    params.row as AgentCoachingService.AgentCoachingData,
                );
            // A disabled checkbox never fires hover events, so the explanation
            // lives on the row instead of on any single cell.
            newDgStore.getRowTooltip = (row) =>
                store.isReadyToCoachRowSelectable(
                    row as AgentCoachingService.AgentCoachingData,
                )
                    ? undefined
                    : alreadyInDraftMessage;
        } else if (tabName !== "MyDrafts") {
            newDgStore.checkboxSelection = false;
            newDgStore.disableRowSelectionOnClick = true;
        }

        newDgStore.disableLocalConfig = true;
        if (
            tabName === "ReadyToCoach" ||
            tabName === "Evaluations" ||
            tabName === "CompletedCoaching"
        ) {
            newDgStore.rowHeightOverride = true;
        }

        let cols: IColDef[] = [];

        const scoresCol: IColDef = {
            headerName: "Scores",
            field: "scores",
            flex: 1,
            headerAlign: "left",
            renderCell(params) {
                return (
                    <AcxChipList
                        size={"small"}
                        color={"blue"}
                        values={params.row.scores?.map(
                            (score) =>
                                `${score.moduleName}: ${(
                                    score.avgScore * 100
                                ).toFixed(0)}%`,
                        )}
                    />
                );
            },
            valueGetter: (_, row: AgentCoachingService.AgentCoachingData) => {
                if (!row.scores?.length) return undefined;

                return (
                    row.scores.reduce(
                        (total, score) => total + score.avgScore,
                        0,
                    ) / row.scores.length
                );
            },
            type: "number",
        };

        const evalNumberLinkCol: IColDef = {
            field: "evaluationQbId",
            headerName: "Evaluation",
            flex: 1,
            renderCell: (params: GridRenderCellParams) => {
                const orgId = store.authStore.orgStore.selectedOrganization?.id;
                const evalId = params.row.id;
                const evaluationQbIds = Array.isArray(
                    params.row.evaluationQbIds,
                )
                    ? params.row.evaluationQbIds.filter(Boolean)
                    : [];
                const singleEvaluationQbId =
                    params.row.evaluationQbId ??
                    (evaluationQbIds.length === 1
                        ? evaluationQbIds[0]
                        : undefined);

                if (!singleEvaluationQbId && evaluationQbIds.length === 0) {
                    return null;
                }

                if (evaluationQbIds.length > 1) {
                    const evaluationIds = Array.isArray(
                        params.row.evaluationIds,
                    )
                        ? params.row.evaluationIds.filter(Boolean)
                        : [];

                    if (evaluationIds.length !== evaluationQbIds.length) {
                        return null;
                    }
                    const linkIds = evaluationIds;

                    return (
                        <Box
                            sx={{
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                lineHeight: 1.5,
                            }}
                        >
                            {evaluationQbIds.map((evaluationQbId, index) => {
                                const linkedEvalId = linkIds[index];
                                const content = linkedEvalId ? (
                                    <Link
                                        className="pendo-ignore"
                                        to={Routes.makeEvaluationRoute(
                                            orgId ?? "",
                                            linkedEvalId,
                                        )}
                                    >
                                        {evaluationQbId}
                                    </Link>
                                ) : (
                                    evaluationQbId
                                );

                                return (
                                    <React.Fragment
                                        key={`${
                                            linkedEvalId ?? evaluationQbId
                                        }-${index}`}
                                    >
                                        {content}
                                        {index < evaluationQbIds.length - 1
                                            ? ", "
                                            : ""}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    );
                }

                return (
                    <Link
                        className="pendo-ignore"
                        to={Routes.makeEvaluationRoute(
                            orgId ?? "",
                            evalId ?? "",
                        )}
                    >
                        {singleEvaluationQbId}
                    </Link>
                );
            },
            minWidth: 100,
        };

        const interactionDateCol: IColDef = {
            field: "interactionDate",
            headerName: "Interaction Date",
            flex: 1,
            ...dateTimeColumnType,
        };

        const evalCompleteDateCol: IColDef = {
            field: "submittedDate",
            headerName: "Evaluation Complete Date",
            flex: 1,
            hide: true,
            ...dateTimeColumnType,
        };

        const callDurationCol: IColDef = {
            headerName: "Call Duration",
            field: "callDurationMillis",
            minWidth: 120,
            hide: true,
            ...durationColumnType,
        };

        const notesRenderCell = (params) => {
            if (!params.value) return null;

            return (
                <Box
                    sx={{
                        whiteSpace: "normal", // allow text wrapping
                        overflowWrap: "break-word",
                    }}
                >
                    {params.value}
                </Box>
            );
        };

        const booleanRenderCell = (params) => {
            return !!params.value ? (
                <CheckIcon color="primary" />
            ) : (
                <ClearIcon color="error" />
            );
        };

        const coachingAckBoolCol: IColDef = {
            field: "acknowledged",
            headerName: "Coaching Acknowledged",
            flex: 1,
            type: "boolean",
            renderCell: booleanRenderCell,
        };

        const interactionTypeCol: IColDef = {
            headerName: "Interaction Type",
            field: "interactionType",
            hide: true,
            renderCell: (params) => {
                return (
                    <>
                        {
                            getInteractionType(
                                Number(params.row.interactionType),
                            )?.text
                        }
                    </>
                );
            },
        };

        switch (tabName) {
            case "ReadyToCoach":
                cols = [
                    { ...evalNumberLinkCol },
                    { ...interactionDateCol },
                    { ...evalCompleteDateCol },
                    ...(store.authStore.canUserView("Module Scores")
                        ? [{ ...scoresCol }]
                        : []),
                    { ...callDurationCol },
                    { ...interactionTypeCol },
                ];
                break;
            case "Evaluations":
                cols.push(
                    { ...evalNumberLinkCol },
                    {
                        field: "agentCoachingId",
                        headerName: "Coached",
                        flex: 1,
                        maxWidth: 100,
                        type: "boolean",
                        renderCell: booleanRenderCell,
                    },
                    { ...coachingAckBoolCol },
                );
                if (store.authStore.canUserView("Agent Acknowledge")) {
                    cols.push({
                        headerName: "Evaluation Acknowledged",
                        field: "evalAcknowledged",
                        type: "boolean",
                        flex: 1,
                        renderCell: booleanRenderCell,
                    });
                }
                if (store.authStore.canUserView("Module Scores")) {
                    cols.push(scoresCol);
                }
                cols.push(
                    { ...interactionDateCol },
                    { ...evalCompleteDateCol },
                    { ...callDurationCol },
                    { ...interactionTypeCol },
                );

                break;
            case "CompletedCoaching":
                if (
                    store.authStore.isLoggedInUserAgent() &&
                    store.authStore.canUserView("Agent Acknowledge")
                ) {
                    const ackButtonCol: IColDef = {
                        headerName: "Acknowledge",
                        field: "acknowledge",
                        renderCell: (params) => {
                            if (params.row.acknowledged) {
                                return <></>;
                            }

                            return (
                                <Grid
                                    item
                                    container
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    height={"100%"}
                                >
                                    {/* Not using AcxButton because it does not support Link component prop */}
                                    <Button
                                        variant={"outlined"}
                                        size={"small"}
                                        disableElevation
                                        id={"agent-acknowledge-button"}
                                        onClick={() => {
                                            store.reviewSingleEvaluation(
                                                params.row.id,
                                                true,
                                            );
                                        }}
                                        component={Link}
                                        to="/app/agentcoaching/evaluationreview"
                                    >
                                        Acknowledge
                                    </Button>
                                </Grid>
                            );
                        },
                        minWidth: 150,
                    };
                    cols.push({ ...ackButtonCol });
                }

                if (
                    store.authStore.canUserEdit("Agent Coaching") &&
                    !store.authStore.isLoggedInUserAgent()
                ) {
                    const conversationCol: IColDef = {
                        headerName: "Conversation",
                        field: "conversation",
                        renderCell: (params: GridRenderCellParams) => {
                            const amdIds = Array.isArray(params.row.amdIds)
                                ? params.row.amdIds.filter(Boolean)
                                : [];
                            const hasMultipleAmds = amdIds.length > 1;
                            const amdId = params.row.amdId;
                            if (!amdId && !hasMultipleAmds) return null;

                            const route = amdId
                                ? Routes.makeConversationsRoute(amdId) +
                                  "/" +
                                  Routes.EVAL_COACHING
                                : "#";

                            return (
                                <Grid
                                    item
                                    container
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    height={"100%"}
                                >
                                    <Tooltip
                                        title={
                                            hasMultipleAmds
                                                ? "Disabled when coaching has multiple conversations"
                                                : ""
                                        }
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                variant={"outlined"}
                                                size={"small"}
                                                disableElevation
                                                disabled={hasMultipleAmds}
                                                component={Link}
                                                to={route}
                                            >
                                                View
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            );
                        },
                        minWidth: 130,
                        sortable: false,
                        filterable: false,
                    };
                    cols.push({ ...conversationCol });
                }

                cols.push(
                    { ...coachingAckBoolCol },
                    { ...evalNumberLinkCol },
                    { ...evalCompleteDateCol },
                    {
                        headerName: "Improvement Notes",
                        field: "improvementNotes",
                        renderCell: notesRenderCell,
                        flex: 1,
                        minWidth: 200,
                    },
                    {
                        headerName: "Does Well",
                        field: "agentDoWellNotes",
                        renderCell: notesRenderCell,
                        flex: 1,
                        minWidth: 200,
                    },
                    {
                        headerName: "Focus Areas",
                        field: "focusAreas",
                        minWidth: 150,
                        renderCell: (params) => {
                            if (!params.value) return null;
                            const parsed = JSON.parse(params.value);

                            return (
                                <Stack
                                    height={"100%"}
                                    justifyContent={"center"}
                                    alignContent={"center"}
                                >
                                    <Stack maxHeight={"max-content"}>
                                        <AcxChipList
                                            size={"small"}
                                            color={"blue"}
                                            values={parsed}
                                            height="100%"
                                            toolTip={parsed.join(", ")}
                                        />
                                    </Stack>
                                </Stack>
                            );
                        },
                    },
                    {
                        headerName: "Response Notes",
                        field: "responseNotes",
                        renderCell: notesRenderCell,
                    },
                    {
                        field: "coach",
                        headerName: "Coach",
                    },
                    {
                        headerName: "Coached On",
                        field: "coachedOn",
                        minWidth: 165,
                        ...dateTimeColumnType,
                    },
                    { ...callDurationCol },
                    { ...interactionTypeCol },
                );

                break;
            case "MyDrafts":
                cols = [
                    {
                        headerName: "Evaluation",
                        field: "targets",
                        renderCell: (params) => {
                            // The draft's own targets carry the evaluation numbers, so
                            // this renders regardless of the loaded eval dataset.
                            const evaluations = (
                                (params.row.targets ??
                                    []) as AgentCoachingTarget[]
                            ).filter((target) => !!target.evaluationId);
                            if (!evaluations.length) return null;

                            return (
                                <Grid
                                    container
                                    columnSpacing={1 / 4}
                                    direction="row"
                                    justifyContent="start"
                                >
                                    {evaluations.map((evaluation, index) => {
                                        const orgId =
                                            store.authStore.orgStore
                                                .selectedOrganization?.id;

                                        // Each number needs its own Grid item —
                                        // columnSpacing only pads item children, so
                                        // bare links run together as one number.
                                        return (
                                            <Grid
                                                item
                                                key={evaluation.evaluationId}
                                            >
                                                <Link
                                                    className="pendo-ignore"
                                                    to={Routes.makeEvaluationRoute(
                                                        orgId ?? "",
                                                        evaluation.evaluationId!,
                                                    )}
                                                >
                                                    {evaluation.evaluationQbId}
                                                </Link>
                                                {index < evaluations.length - 1
                                                    ? ","
                                                    : ""}
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            );
                        },
                        flex: 1,
                    },
                    {
                        headerName: "Last Modified",
                        field: "modifiedOn",
                        flex: 1,
                        ...dateTimeColumnType,
                    },
                    {
                        headerName: "Edit",
                        field: "id",

                        renderCell: (params) => {
                            const coachingRoute = store.getDraftEditRoute(
                                params.row,
                            );
                            if (!coachingRoute) return null;

                            // Coaching that spans several targets, and Live
                            // evaluations, are reviewed on the standalone page,
                            // which needs the store primed before navigating. A
                            // single conversation primes itself on load.
                            const needsEvalReviewPage =
                                store.draftNeedsEvaluationReviewPage(
                                    params.row,
                                );

                            return (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    justifyContent={"center"}
                                    alignContent={"center"}
                                    height={"100%"}
                                >
                                    <Button
                                        variant={"contained"}
                                        size={"small"}
                                        color="primary"
                                        disableElevation
                                        id={"edit-coaching-button"}
                                        onClick={() => {
                                            if (!needsEvalReviewPage) return;
                                            // Need an actual instance of the AgentCoaching class since we use the methods on it
                                            // inside of eval review.
                                            const hydratedDraft = Object.assign(
                                                new AgentCoaching(),
                                                params.row,
                                            );
                                            store.reviewDraft(hydratedDraft);
                                        }}
                                        component={Link}
                                        to={coachingRoute}
                                        startIcon={<Edit />}
                                    >
                                        Edit
                                    </Button>
                                </Grid>
                            );
                        },
                        width: 110,
                        resizable: false,
                    },
                ];
                break;
        }

        newDgStore.setColumns([...cols]);
        store.dgStoreMap[tabName] = newDgStore;
    });
}
